import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ViewInvoiceModal.css";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
// Make sure to add these images to your project and update the paths
import headerImage from "../../../Images/header-logo.png";
import footerImage from "../../../Images/footer-logo.png";
import { toast } from "react-toastify";
import { Toast } from "bootstrap";
import { FaDownload, FaMoneyBillWave, FaPlus, FaTimes, FaShareAlt } from "react-icons/fa";
import SkeletonLoader from "../../resuableComponents/SkeletonLoader";
import CustomDropdown from "../../resuableComponents/CustomDropdown";

const ViewInvoiceModal = ({ appointmentId, onClose, isIPD, billType }) => {
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [refundNote, setRefundNote] = useState("");
  const authData = JSON.parse(localStorage.getItem("authData"));
  const paymentMethodOptions = [
    { label: "Cash", value: "cash" },
    { label: "Card", value: "card" },
    { label: "UPI", value: "upi" },
  ];


  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        const endpoint = isIPD ? "get-ipd-invoice" : "get-invoice";
        const idQuery = isIPD
          ? `admissionId=${appointmentId}`
          : `appointmentId=${appointmentId}`;
        const typeQuery = billType ? `&invoiceType=${billType}` : "";

        let response;
        try {
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}${endpoint}?${idQuery}${typeQuery}`,
            {
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        } catch (requestError) {
          if (!billType || requestError.response?.status !== 404) {
            throw requestError;
          }
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}${endpoint}?${idQuery}`,
            {
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        // Handle both IPD and OPD response structures
        const invoiceData = isIPD
          ? response.data.invoice
          : response.data.invoice[0];
        setInvoiceDetails(invoiceData);
      } catch (error) {
        console.error("Error fetching invoice details:", error);
        setError("Failed to load invoice details");
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      fetchInvoiceDetails();
    }
  }, [appointmentId, success, isIPD, billType]);

  const handleAddDiscount = async () => {
    if (!discount || isNaN(discount)) {
      setError("Please enter a valid discount amount");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const discountEndpoint = isIPD ? "add-ipd-discount" : "add-discount";

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}${discountEndpoint}`,
        {
          invoiceId: invoiceDetails._id,
          discount: Number(discount),
        },
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        }
      );

      setSuccess("Discount applied successfully!");
      toast.success("Discount applied successfully!");
      setDiscount("");
      setShowDiscountInput(false);
    } catch (error) {
      console.error("Error applying discount:", error);
      setError("Failed to apply discount. Please try again.");
      toast.error("Failed to apply discount. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || isNaN(paymentAmount)) {
      setError("Please enter a valid amount");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const paymentEndpoint = isIPD ? "add-ipd-payment" : "add-payment";

    // Build the base payload
    const payload = {
      invoiceId: invoiceDetails._id,
      payment: {
        amount: Number(paymentAmount),
        method: paymentMethod,
      },
    };

    // Add admissionId if IPD
    if (isIPD && invoiceDetails?.admission?._id) {
      payload.admissionId = invoiceDetails.admission._id;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}${paymentEndpoint}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        }
      );

      setSuccess("Payment added successfully!");
      toast.success("Payment added successfully!");
      setPaymentAmount("");
      setShowAddPayment(false);
    } catch (error) {
      console.error("Error adding payment:", error);
      setError("Failed to add payment. Please try again.");
      toast.error("Failed to add payment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="view-invoice-modal-overlay">
        <div className="view-invoice-modal-content">
          <SkeletonLoader variant="modal" />
        </div>
      </div>
    );

  if (!invoiceDetails || typeof invoiceDetails !== 'object')
    return (
      <div
        className="modal-overlay display-flex justify-content-center align-items-center"
        style={{ flexDirection: "row" }}
      >
        <div className="view-invoice-modal-content">
          <p>No invoice details found or invalid data</p>
          <p style={{ fontSize: '0.8rem', color: '#999' }}>
            AppointmentId: {appointmentId}
          </p>
          <button type="button" className="view-invoice-modal-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>
      </div>
    );

  // Add this at the beginning of your generatePDF function
  const sanitizeNumber = (num) => {
    if (typeof num === "string") {
      // Remove any non-numeric characters except decimal point
      return parseFloat(num.replace(/[^\d.-]/g, ""));
    }
    return num;
  };

  const generatePDFAction = async (action = 'download') => {
    if (!invoiceDetails) return;

    // Initialize PDF with proper settings
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Original color theme - using RGB arrays for jsPDF compatibility
    const primaryColor = [25, 135, 84]; // #198754 in RGB
    const secondaryColor = [248, 249, 250]; // #f8f9fa in RGB
    const textColor = [33, 37, 41]; // #212529 in RGB
    const whiteColor = [255, 255, 255];

    // Dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const headerHeight = 25; // Space reserved for header
    const footerHeight = 25; // Space reserved for footer
    let yPos = headerHeight + 10; // Start position after header

    // Modern fonts
    doc.setFont("helvetica", "normal");

    // Helper function to check if there's enough space for a section
    const ensureSpace = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - footerHeight) {
        doc.addPage();
        addHeaderFooter({ pageNumber: doc.internal.getNumberOfPages() });
        yPos = headerHeight + 10;
      }
    };

    // Function to add header and footer to all pages
    const addHeaderFooter = (data) => {
      // Header image - full width
      if (headerImage) {
        doc.addImage(headerImage, "PNG", 0, 0, pageWidth, headerHeight);
      }

      // Footer image - full width
      if (footerImage) {
        doc.addImage(
          footerImage,
          "PNG",
          0,
          pageHeight - footerHeight,
          pageWidth,
          footerHeight
        );
      }
    };

    // Apply header/footer to first page
    addHeaderFooter({ pageNumber: 1 });

    // Invoice title - modern styling
    ensureSpace(25);
    doc.setFillColor(...primaryColor);
    doc.rect(margin, yPos - 10, pageWidth - margin * 2, 2, "F");

    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(
      invoiceDetails.invoiceType === "medicine"
        ? "MEDICINE INVOICE"
        : invoiceDetails.invoiceType === "treatment"
          ? "TREATMENT INVOICE"
          : "INVOICE",
      margin,
      yPos
    );
    yPos += 15;

    // Invoice metadata - cleaner layout
    ensureSpace(25);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textColor);

    // Left side metadata with null checks
    doc.text(`Invoice No. : ${invoiceDetails._id || "N/A"}`, margin, yPos);

    // Show appointment or admission number based on type
    if (isIPD && invoiceDetails.admission) {
      doc.text(
        `Admission No. : ${invoiceDetails.admission._id || "N/A"}`,
        margin,
        yPos + 6
      );
    } else {
      doc.text(
        `Appointment No. : ${invoiceDetails.appointment || "N/A"}`,
        margin,
        yPos + 6
      );
    }

    // Right side metadata
    const dateStr = dayjs(invoiceDetails.createdAt).format("DD/MM/YYYY");
    doc.text(`Date: ${dateStr}`, pageWidth - margin, yPos, { align: "right" });

    // Status with colored background
    const statusText = invoiceDetails.paid ? "PAID" : "PENDING";
    const statusColor = invoiceDetails.paid ? primaryColor : [255, 159, 67]; // Orange for pending

    doc.setFillColor(...statusColor);
    const statusWidth = doc.getStringUnitWidth(statusText) * 10 * 0.352778 + 10;
    doc.roundedRect(
      pageWidth - margin - statusWidth,
      yPos + 2,
      statusWidth,
      10,
      2,
      2,
      "F"
    );

    doc.setTextColor(...whiteColor);
    doc.setFont("helvetica", "bold");
    doc.text(statusText, pageWidth - margin - statusWidth / 2, yPos + 8.5, {
      align: "center",
    });

    yPos += 25;

    // Patient information - modern card style
    ensureSpace(50); // Increased for potential IPD info
    doc.setFillColor(...secondaryColor);
    doc.roundedRect(
      margin,
      yPos,
      pageWidth - margin * 2,
      isIPD ? 40 : 30,
      3,
      3,
      "F"
    );

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("PATIENT INFORMATION", margin + 8, yPos + 10);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textColor);

    // Two column layout for patient info
    const colWidth = (pageWidth - margin * 2 - 16) / 2;

    // Patient info differs based on isIPD with careful null handling
    let patientName = "Unknown";
    let patientContact = "N/A";
    let patientId = "N/A";

    try {
      if (
        isIPD &&
        invoiceDetails.admission &&
        invoiceDetails.admission.patient
      ) {
        patientName = invoiceDetails.admission.patient.name || "Unknown";
        patientContact = invoiceDetails.admission.patient.contact || "N/A";
        patientId = invoiceDetails.admission.patient.patientId || "N/A";
      } else if (invoiceDetails.patient) {
        patientName = invoiceDetails.patient.name || "Unknown";
        patientContact = invoiceDetails.patient.contact || "N/A";
        patientId = invoiceDetails.patient.patientId || "N/A";
      }
    } catch (e) {
      console.error("Error accessing patient details:", e);
    }

    doc.text(`Name: ${patientName}`, margin + 8, yPos + 18);
    doc.text(`Contact: ${patientContact}`, margin + 8, yPos + 25);
    doc.text(`Patient ID: ${patientId}`, margin + colWidth + 8, yPos + 18);

    // Add admission details for IPD with null checks
    if (isIPD && invoiceDetails.admission) {
      try {
        const admissionDate = invoiceDetails.admission.admissionDate
          ? dayjs(invoiceDetails.admission.admissionDate).format("DD/MM/YYYY")
          : "N/A";
        const roomNumber = invoiceDetails.admission.roomNumber || "N/A";
        const bedNumber = invoiceDetails.admission.bedNumber || "N/A";

        doc.text(`Admission Date: ${admissionDate}`, margin + 8, yPos + 32);
        doc.text(
          `Room: ${roomNumber} (Bed: ${bedNumber})`,
          margin + colWidth + 8,
          yPos + 25
        );
      } catch (e) {
        console.error("Error displaying admission details:", e);
        doc.text("Admission details unavailable", margin + 8, yPos + 32);
      }
      yPos += 50; // Extra space for IPD info
    } else {
      yPos += 40; // Original spacing for OPD
    }

    // Helper function for consistent modern number formatting
    const formatCurrency = (amount) => {
      const sanitizedAmount = parseFloat(amount) || 0;
      return `Rs. ${sanitizedAmount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    // Add Room and Nursing charges for IPD with null safety
    if (isIPD && invoiceDetails.invoiceType !== "medicine") {
      ensureSpace(50);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...primaryColor);
      doc.text("ROOM & NURSING CHARGES", margin, yPos);
      yPos += 7;

      // Safely extract room and nursing charge data with defaults
      const roomCharges = {
        perDayRate: invoiceDetails.roomCharges?.perDayRate || 0,
        totalDays: invoiceDetails.roomCharges?.totalDays || 0,
        total: invoiceDetails.roomCharges?.total || 0,
      };

      const nursingCharges = {
        perDayRate: invoiceDetails.nursingCharges?.perDayRate || 0,
        totalDays: invoiceDetails.nursingCharges?.totalDays || 0,
        total: invoiceDetails.nursingCharges?.total || 0,
      };

      const availableWidth = pageWidth - margin * 2;

      const columnWidths = {
        0: availableWidth * 0.3, // Description (30% of available width)
        1: availableWidth * 0.25, // Rate/Day (25%)
        2: availableWidth * 0.2, // Days (20%)
        3: availableWidth * 0.25, // Total (25%)
      };

      autoTable(doc, {
        startY: yPos,
        head: [["Description", "Rate/Day", "Days", "Total"]],
        body: [
          [
            "Room Charges",
            formatCurrency(roomCharges.perDayRate),
            roomCharges.totalDays,
            formatCurrency(roomCharges.total),
          ],
          [
            "Nursing Charges",
            formatCurrency(nursingCharges.perDayRate),
            nursingCharges.totalDays,
            formatCurrency(nursingCharges.total),
          ],
        ],
        margin: {
          left: margin,
          right: margin,
        },
        styles: {
          fontSize: 10,
          font: "helvetica",
          cellPadding: 5,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: primaryColor,
          textColor: whiteColor,
          fontStyle: "bold",
          valign: "middle", // Vertically center the text
          cellPadding: { top: 5, bottom: 5 }, // Consistent padding
        },
        columnStyles: {
          0: { cellWidth: columnWidths[0], halign: "center" }, // Description: Left-aligned
          1: { cellWidth: columnWidths[1], halign: "center" }, // Rate/Day: Right-aligned
          2: { cellWidth: columnWidths[2], halign: "center" }, // Days: Center-aligned
          3: { cellWidth: columnWidths[3], halign: "center" }, // Total: Right-aligned
        },
        didParseCell: (data) => {
          // Apply alignments to header cells
          if (data.section === "head") {
            if (data.column.index === 0) data.cell.styles.halign = "center"; // Description
            if (data.column.index === 1) data.cell.styles.halign = "center"; // Rate/Day
            if (data.column.index === 2) data.cell.styles.halign = "center"; // Days
            if (data.column.index === 3) data.cell.styles.halign = "center"; // Total
          }
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        tableWidth: "auto", // Takes full width based on column widths
        didDrawPage: (data) => {
          addHeaderFooter({ pageNumber: doc.internal.getNumberOfPages() });
          yPos = data.cursor.y;
        },
      });

      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Treatments table - modern styling
    const availableWidth = pageWidth - margin * 2;
    if (invoiceDetails.invoiceType !== "medicine") {
    ensureSpace(50);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("TREATMENTS", margin, yPos);
    yPos += 7;

    // Make sure treatments is an array with safe access
    const treatments = Array.isArray(invoiceDetails.treatments)
      ? invoiceDetails.treatments
      : [];
    const columnWidths = {
      0: availableWidth * 0.7, // Treatment
      1: availableWidth * 0.3, // Amount
    };

    autoTable(doc, {
      startY: yPos,
      head: [["Treatment", "Amount"]],
      body: treatments.map((t) => [
        t.name || "Unknown treatment",
        formatCurrency(t.cost || 0),
      ]),
      margin: {
        left: margin,
        right: margin,
        top: headerHeight,
        bottom: footerHeight,
      },
      styles: {
        fontSize: 10,
        font: "helvetica",
        cellPadding: 5, // Global padding, can be overridden by headStyles and bodyStyles
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: whiteColor,
        fontStyle: "bold",
        valign: "middle",
        halign: "center",
        cellPadding: { top: 5, bottom: 5, left: 10, right: 5 }, // Left padding for header
      },
      bodyStyles: {
        valign: "middle",
        cellPadding: { top: 5, bottom: 5, left: 10, right: 5 }, // Add left padding for body
      },
      columnStyles: {
        0: { cellWidth: columnWidths[0], halign: "left" },
        1: { cellWidth: columnWidths[1], halign: "left", fontStyle: "bold" },
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.halign = "left";
        }
      },
      didDrawPage: (data) => {
        addHeaderFooter({ pageNumber: data.pageCount });
        yPos = headerHeight + 10;
        data.settings.startY = yPos;
      },
      willDrawCell: (data) => {
        if (data.cell.y + data.cell.height > pageHeight - footerHeight) {
          data.cell.y = pageHeight - footerHeight - data.cell.height;
        }
      },
      tablePageBreak: "auto",
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Services table - modern styling
    ensureSpace(50);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("SERVICES", margin, yPos);
    yPos += 7;

    // Make sure services is an array with safe access
    const services = Array.isArray(invoiceDetails.services)
      ? invoiceDetails.services
      : [];

    const columnWidthsServices = {
      0: availableWidth * 0.7, // Service
      1: availableWidth * 0.3, // Amount
    };

    autoTable(doc, {
      startY: yPos,
      head: [["Service", "Amount"]],
      body: services.length > 0
        ? services.map((s) => [
          s.name || "Unknown service",
          formatCurrency(s.cost || 0),
        ])
        : [["No services", formatCurrency(0)]],
      margin: {
        left: margin,
        right: margin,
        top: headerHeight,
        bottom: footerHeight,
      },
      styles: {
        fontSize: 10,
        font: "helvetica",
        cellPadding: 5,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: whiteColor,
        fontStyle: "bold",
        valign: "middle",
        halign: "center",
        cellPadding: { top: 5, bottom: 5, left: 10, right: 5 },
      },
      bodyStyles: {
        valign: "middle",
        cellPadding: { top: 5, bottom: 5, left: 10, right: 5 },
      },
      columnStyles: {
        0: { cellWidth: columnWidthsServices[0], halign: "left" },
        1: { cellWidth: columnWidthsServices[1], halign: "left", fontStyle: "bold" },
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.halign = "left";
        }
      },
      didDrawPage: (data) => {
        addHeaderFooter({ pageNumber: data.pageCount });
        yPos = headerHeight + 10;
        data.settings.startY = yPos;
      },
      willDrawCell: (data) => {
        if (data.cell.y + data.cell.height > pageHeight - footerHeight) {
          data.cell.y = pageHeight - footerHeight - data.cell.height;
        }
      },
      tablePageBreak: "auto",
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Medicines table - modern styling
    }
    if (invoiceDetails.invoiceType !== "treatment") {
    ensureSpace(50);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("MEDICINES", margin, yPos);
    yPos += 7;

    // Make sure medicines is an array with safe access
    const medicines = Array.isArray(invoiceDetails.medicines)
      ? invoiceDetails.medicines
      : [];

    const columnWidths1 = {
      0: availableWidth * 0.3, // Medicine
      1: availableWidth * 0.15, // Qty
      2: availableWidth * 0.25, // Price
      3: availableWidth * 0.3, // Total
    };

    autoTable(doc, {
      startY: yPos,
      head: [["Medicine", "Qty", "Price", "Total"]],
      body: medicines.map((m) => [
        m.name || "Unknown medicine",
        m.quantity || 1,
        formatCurrency(m.cost || 0),
        formatCurrency((m.cost || 0) * (m.quantity || 1)),
      ]),
      margin: {
        left: margin,
        right: margin,
        top: headerHeight,
        bottom: footerHeight,
      },
      styles: {
        fontSize: 10,
        font: "helvetica",
        cellPadding: 5,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: whiteColor,
        fontStyle: "bold",
        valign: "middle",
        cellPadding: { top: 5, bottom: 5 },
      },
      bodyStyles: {
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: columnWidths1[0], halign: "center" },
        1: { cellWidth: columnWidths1[1], halign: "center" },
        2: { cellWidth: columnWidths1[2], halign: "center" },
        3: { cellWidth: columnWidths1[3], halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.halign = "center";
        }
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      didDrawPage: (data) => {
        addHeaderFooter({ pageNumber: data.pageCount });
        yPos = headerHeight + 10;
        data.settings.startY = yPos;
      },
      willDrawCell: (data) => {
        if (data.cell.y + data.cell.height > pageHeight - footerHeight) {
          data.cell.y = pageHeight - footerHeight - data.cell.height;
        }
      },
      tablePageBreak: "auto",
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Other charges - modern styling
    }
    ensureSpace(50);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("OTHER CHARGES", margin, yPos);
    yPos += 7;

    const otherChargesData = [
      ...(invoiceDetails.invoiceType === "medicine"
        ? []
        : [["Doctor Fee", formatCurrency(invoiceDetails.doctorFee)]]),
      ["Discount", `-${formatCurrency(invoiceDetails.discount || 0)}`],
    ];

    autoTable(doc, {
      startY: yPos,
      body: otherChargesData,
      margin: {
        left: pageWidth / 2,
        right: margin,
        top: headerHeight,
        bottom: footerHeight,
      },
      styles: {
        fontSize: 10,
        cellPadding: 5,
        font: "helvetica",
        lineColor: [220, 220, 220],
      },
      columnStyles: {
        0: { fontStyle: "bold", halign: "left" },
        1: { halign: "right", fontStyle: "bold" },
      },
      didDrawPage: (data) => {
        addHeaderFooter({ pageNumber: data.pageCount });
        yPos = headerHeight + 10;
        data.settings.startY = yPos;
      },
      willDrawCell: (data) => {
        if (data.cell.y + data.cell.height > pageHeight - footerHeight) {
          data.cell.y = pageHeight - footerHeight - data.cell.height;
        }
      },
      tablePageBreak: "auto",
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Payment history - modern styling
    ensureSpace(50);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text("PAYMENT HISTORY", margin, yPos);
    yPos += 7;

    // Make sure payments is an array with safe access
    const payments = Array.isArray(invoiceDetails.payments)
      ? invoiceDetails.payments
      : [];

    const columnWidths2 = {
      0: availableWidth * 0.3, // Date
      1: availableWidth * 0.4, // Method
      2: availableWidth * 0.3, // Amount
    };

    autoTable(doc, {
      startY: yPos,
      head: [["Date", "Method", "Amount"]],
      body: payments.map((p) => {
        let dateStr = "N/A";
        try {
          if (p.date) {
            dateStr = dayjs(p.date).format("DD/MM/YYYY");
          }
        } catch (e) {
          console.error("Error formatting payment date:", e);
        }

        const method = p.method
          ? p.method.charAt(0).toUpperCase() + p.method.slice(1)
          : "Unknown";

        return [dateStr, method, formatCurrency(p.amount || 0)];
      }),
      margin: {
        left: margin,
        right: margin,
        top: headerHeight,
        bottom: footerHeight,
      },
      styles: {
        fontSize: 10,
        font: "helvetica",
        cellPadding: 5,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: whiteColor,
        fontStyle: "bold",
        valign: "middle",
        cellPadding: { top: 5, bottom: 5 },
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      columnStyles: {
        0: { cellWidth: columnWidths2[0], halign: "center" }, // Date
        1: { cellWidth: columnWidths2[1], halign: "center" }, // Method
        2: { cellWidth: columnWidths2[2], halign: "center", fontStyle: "bold" }, // Amount
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.halign = "center";
        }
      },
      didDrawPage: (data) => {
        addHeaderFooter({ pageNumber: data.pageCount });
        yPos = headerHeight + 10;
        data.settings.startY = yPos;
      },
      willDrawCell: (data) => {
        if (data.cell.y + data.cell.height > pageHeight - footerHeight) {
          data.cell.y = pageHeight - footerHeight - data.cell.height;
        }
      },
      tablePageBreak: "auto",
    });

    yPos = doc.lastAutoTable.finalY + 20;

    // Check if totals section would overlap with footer
    ensureSpace(45);

    // Simple clean total section
    const totalWidth = 100;
    const totalX = pageWidth - margin - totalWidth;

    // Draw a clean background box
    doc.setFillColor(...secondaryColor);
    doc.rect(totalX, yPos, totalWidth, 45, "F");

    // Left side blue accent
    doc.setFillColor(...primaryColor);
    doc.rect(totalX, yPos, 10, 45, "F");

    // Totals content
    doc.setFontSize(11);
    doc.setTextColor(...textColor);
    doc.setFont("helvetica", "normal");

    // Label column
    const labelX = totalX + 15;
    doc.text("Total Amount:", labelX, yPos + 15);
    doc.text("Amount Paid:", labelX, yPos + 30);

    // Values column - right aligned with fixed width for consistent alignment with null safety
    const valueX = totalX + totalWidth - 10;
    doc.setFont("helvetica", "bold");
    doc.text(
      formatCurrency(invoiceDetails.totalAmount || 0),
      valueX,
      yPos + 15,
      {
        align: "right",
      }
    );
    doc.text(
      formatCurrency(invoiceDetails.amountPaid || 0),
      valueX,
      yPos + 30,
      {
        align: "right",
      }
    );

    // Balance due
    doc.setFillColor(...primaryColor);
    doc.rect(totalX, yPos + 35, totalWidth, 10, "F");

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...whiteColor);
    doc.text("Balance Due:", labelX, yPos + 42);
    doc.text(
      formatCurrency(invoiceDetails.balanceDue || 0),
      valueX,
      yPos + 42,
      {
        align: "right",
      }
    );

    const extraBalance = Number(
      invoiceDetails.extraBalance ??
        Math.max((invoiceDetails.amountPaid || 0) - (invoiceDetails.totalAmount || 0), 0)
    );
    if (extraBalance > 0) {
      const savedRefunds = Array.isArray(invoiceDetails.refunds) ? invoiceDetails.refunds : [];
      const refundedTotal = savedRefunds.reduce((sum, refund) => sum + Number(refund.amount || 0), 0);
      const stillHeld = Math.max(extraBalance - refundedTotal, 0);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(154, 52, 18);
      doc.text(`Extra Balance: ${formatCurrency(extraBalance)}`, labelX, yPos + 60);
      if (refundedTotal > 0) {
        doc.text(`Returned to patient: ${formatCurrency(refundedTotal)}`, labelX, yPos + 72);
        doc.text(`Still with hospital: ${formatCurrency(stillHeld)}`, labelX, yPos + 84);
      }
    }

    // Save the PDF with a clean filename
    // Use proper patient name reference based on isIPD with fallback for safety
    let patientName1 = "Patient";
    try {
      // Safely access patient name with fallbacks to prevent undefined errors
      if (
        isIPD &&
        invoiceDetails.admission &&
        invoiceDetails.admission.patient
      ) {
        patientName1 = invoiceDetails.admission.patient.name || "Patient";
      } else if (invoiceDetails.patient) {
        patientName1 = invoiceDetails.patient.name || "Patient";
      }
    } catch (e) {
      console.error("Error accessing patient name:", e);
      patientName = "Patient"; // Use default if any error occurs
    }

    // Use a safe method to create the filename
    const safePatientName = patientName.toString().replace(/\s+/g, "_");
    const invoiceId = (invoiceDetails._id || "").toString();

    const fileName = `Invoice_${safePatientName}_${invoiceId}.pdf`;

    if (action === 'download') {
      doc.save(fileName);
    } else if (action === 'share') {
      try {
        const pdfBlob = doc.output('blob');
        const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'Invoice',
            text: `Please find the attached invoice for ${patientName}.`,
            files: [file]
          });
        } else {
          toast.error("Sharing files is not supported on this device/browser.");
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error sharing invoice:", error);
          toast.error("Failed to share invoice.");
        }
      }
    }
  };

  const generatePDF = () => generatePDFAction('download');
  const sharePDF = () => generatePDFAction('share');

  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const safeInvoiceDetails = {
    ...invoiceDetails,
    treatments: invoiceDetails?.treatments || [],
    services: invoiceDetails?.services || [],
    medicines: invoiceDetails?.medicines || [],
    payments: invoiceDetails?.payments || [],
  };

  const invoiceKind = invoiceDetails.invoiceType || "combined";
  const showTreatmentSections = invoiceKind !== "medicine";
  const showMedicineSection = invoiceKind !== "treatment";
  const extraBalance = Number(
    invoiceDetails.extraBalance ??
      Math.max((invoiceDetails.amountPaid || 0) - (invoiceDetails.totalAmount || 0), 0)
  );
  const refunds = Array.isArray(invoiceDetails.refunds) ? invoiceDetails.refunds : [];
  const refundedTotal = refunds.reduce((sum, refund) => sum + Number(refund.amount || 0), 0);
  const remainingExtra = Math.max(extraBalance - refundedTotal, 0);
  const paymentExceedsBalance =
    paymentAmount !== "" && Number(paymentAmount) > Number(invoiceDetails.balanceDue || 0);
  const handleRecordRefund = async () => {
    const amount = Number(refundAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter the amount returned to the patient.");
      return;
    }
    if (amount > remainingExtra) {
      toast.error("Refund cannot be more than the extra balance still held.");
      return;
    }

    const refundEndpoint = isIPD ? "add-ipd-refund" : "add-refund";
    setIsSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}${refundEndpoint}`,
        {
          invoiceId: invoiceDetails._id,
          refund: {
            amount,
            method: refundMethod,
            note: refundNote.trim(),
          },
        },
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        }
      );
      setRefundAmount("");
      setRefundNote("");
      setRefundMethod("cash");
      setShowRefundForm(false);
      toast.success("Refund recorded on this invoice.");
      setSuccess(`Refund recorded ${Date.now()}`);
    } catch (error) {
      console.error("Error recording refund:", error);
      toast.error(error.response?.data?.message || "Failed to record the refund.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const invoiceTitle =
    invoiceKind === "medicine"
      ? "Medicine Invoice"
      : invoiceKind === "treatment"
        ? "Treatment Invoice"
        : "Invoice Details";

  return (
    <>
      <div className="view-invoice-modal-overlay">
        <div className="view-invoice-modal-content">
          <div className="view-invoice-modal-header">
            <h3>{invoiceTitle}</h3>
            <button type="button" className="view-invoice-modal-close-btn" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div className="view-invoice-modal-body">
            {/* {error && <div className="alert alert-danger">{error}</div>} */}
            {/* {success && <div className="alert alert-success">{success}</div>} */}

            <div className="view-invoice-modal-patient-info">
              <h4>Patient Information</h4>
              <p>
                Name:{" "}
                {isIPD
                  ? invoiceDetails.admission?.patient?.name || "N/A"
                  : invoiceDetails.patient?.name || "N/A"}
              </p>
              <p>
                Contact:{" "}
                {isIPD
                  ? invoiceDetails.admission?.patient?.contact || "N/A"
                  : invoiceDetails.patient?.contact || "N/A"}
              </p>
              <p>
                Patient ID:{" "}
                {isIPD
                  ? invoiceDetails.admission?.patient?.patientId || "N/A"
                  : invoiceDetails.patient?.patientId || "N/A"}
              </p>
              {isIPD && invoiceDetails.admission && (
                <>
                  <p>
                    Admission Date:{" "}
                    {invoiceDetails.admission.admissionDate
                      ? dayjs(invoiceDetails.admission.admissionDate).format("DD/MM/YYYY")
                      : "N/A"}
                  </p>
                  <p>
                    Room: {invoiceDetails.admission.roomNumber || "N/A"} (Bed:{" "}
                    {invoiceDetails.admission.bedNumber || "N/A"})
                  </p>
                </>
              )}
            </div>

            {isIPD && showTreatmentSections && invoiceDetails.roomCharges && invoiceDetails.nursingCharges && (
              <>
                <div className="view-invoice-modal-section">
                  <h4>Room Charges</h4>
                  <p>
                    ₹{invoiceDetails.roomCharges?.perDayRate || 0} ×{" "}
                    {invoiceDetails.roomCharges?.totalDays || 0} days = ₹
                    {invoiceDetails.roomCharges?.total || 0}
                  </p>
                </div>

                <div className="view-invoice-modal-section">
                  <h4>Nursing Charges</h4>
                  <p>
                    ₹{invoiceDetails.nursingCharges?.perDayRate || 0} ×{" "}
                    {invoiceDetails.nursingCharges?.totalDays || 0} days = ₹
                    {invoiceDetails.nursingCharges?.total || 0}
                  </p>
                </div>
              </>
            )}

            {showTreatmentSections && (
            <>
            <div className="view-invoice-modal-section">
              <h4>Treatments</h4>
              <ul>
                {safeInvoiceDetails.treatments.filter(Boolean).length > 0 ? (
                  safeInvoiceDetails.treatments.filter(Boolean).map((t, i) => (
                    <li key={i}>
                      {t.name || "Treatment"} - ₹{t.cost || 0}
                    </li>
                  ))
                ) : (
                  <li>No treatments</li>
                )}
              </ul>
            </div>

            {isIPD && (
            <div className="view-invoice-modal-section">
              <h4>Services</h4>
              <ul>
                {safeInvoiceDetails.services.filter(Boolean).length > 0 ? (
                  safeInvoiceDetails.services.filter(Boolean).map((s, i) => (
                    <li key={i}>
                      {s.name || "Service"} - ₹{s.cost || 0}
                    </li>
                  ))
                ) : (
                  <li>No services</li>
                )}
              </ul>
            </div>
            )}

            <div className="view-invoice-modal-section">
              <h4>Doctor Fees</h4>
              <p>₹{invoiceDetails.doctorFee ? invoiceDetails.doctorFee : 0}</p>
            </div>
            </>
            )}

            {showMedicineSection && (
            <div className="view-invoice-modal-section">
              <h4>Medicines</h4>
              <ul>
                {safeInvoiceDetails.medicines.filter(Boolean).length > 0 ? (
                  safeInvoiceDetails.medicines.filter(Boolean).map((m, i) => (
                    <li key={i}>
                      {m.name || "Medicine"} - ₹{m.cost || 0} × {m.quantity || 1} = ₹
                      {((m.cost || 0) * (m.quantity || 1)).toFixed(2)}
                    </li>
                  ))
                ) : (
                  <li>No medicines</li>
                )}
              </ul>
            </div>
            )}

            <div className="view-invoice-modal-section">
              <div>
                <h4>Discount</h4>
                <p>₹{invoiceDetails.discount || 0}</p>
              </div>

              {invoiceDetails.paid === false && (
                <div className="view-invoice-modal-form-group">
                  {!showDiscountInput ? (
                    <button
                      type="button"
                      onClick={() => setShowDiscountInput(true)}
                      className="view-invoice-modal-discount-btn"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <FaPlus style={{ marginRight: 8 }} />
                      Add Discount
                    </button>
                  ) : (
                    <>
                      <label>Discount (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                      />

                      <button
                        type="button"
                        onClick={handleAddDiscount}
                        disabled={isSubmitting}
                        className="view-invoice-modal-submit-discount-btn"
                      >
                        {isSubmitting ? "Applying..." : "Apply Discount"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDiscountInput(false)}
                        className="view-invoice-modal-cancel-discount-btn"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="view-invoice-modal-section">
              <h4>Payments</h4>
              <ul>
                {safeInvoiceDetails.payments.filter(Boolean).length > 0 ? (
                  safeInvoiceDetails.payments.filter(Boolean).map((p, i) => (
                    <li key={i}>
                      {p.method || "N/A"} - ₹{p.amount || 0} on{" "}
                      {p.date ? dayjs(p.date).format("DD/MM/YYYY") : "N/A"}
                    </li>
                  ))
                ) : (
                  <li>No payments</li>
                )}
              </ul>
            </div>

            <div className="view-invoice-modal-totals">
              <div className="view-invoice-modal-total-row">
                <span>Total Amount:</span>
                <span>₹{invoiceDetails.totalAmount}</span>
              </div>
              <div className="view-invoice-modal-total-row">
                <span>Amount Paid:</span>
                <span>₹{invoiceDetails.amountPaid}</span>
              </div>
              <div className="view-invoice-modal-total-row">
                <span>Balance Due:</span>
                <span>₹{invoiceDetails.balanceDue}</span>
              </div>
              {extraBalance > 0 && (
                <div className="view-invoice-modal-total-row view-invoice-modal-extra-balance">
                  <span>Extra Balance:</span>
                  <span>₹{extraBalance}</span>
                </div>
              )}
              {refundedTotal > 0 && (
                <div className="view-invoice-modal-total-row view-invoice-modal-refunded">
                  <span>Returned to patient:</span>
                  <span>₹{refundedTotal}</span>
                </div>
              )}
              {extraBalance > 0 && (
                <div className="view-invoice-modal-total-row view-invoice-modal-extra-left">
                  <span>Still with hospital:</span>
                  <span>₹{remainingExtra}</span>
                </div>
              )}
            </div>

            {extraBalance > 0 && (
              <div className="view-invoice-refund">
                <div className="view-invoice-refund-head">
                  <div>
                    <h4>Return extra balance</h4>
                    <p>
                      {remainingExtra > 0
                        ? `₹${remainingExtra} can still be paid back to the patient.`
                        : "The full extra balance has been returned to the patient."}
                    </p>
                  </div>
                  {remainingExtra > 0 && !showRefundForm && (
                    <button
                      type="button"
                      className="view-invoice-refund-open"
                      onClick={() => setShowRefundForm(true)}
                    >
                      Record refund
                    </button>
                  )}
                </div>

                {refunds.length > 0 && (
                  <ul className="view-invoice-refund-list">
                    {refunds.map((refund, index) => (
                      <li key={`${refund.date || "refund"}-${index}`}>
                        <span>
                          {refund.method || "cash"} · ₹{refund.amount}
                          {refund.note ? ` · ${refund.note}` : ""}
                        </span>
                        <em>{refund.date ? dayjs(refund.date).format("DD MMM YYYY") : "Today"}</em>
                      </li>
                    ))}
                  </ul>
                )}

                {showRefundForm && (
                  <div className="view-invoice-refund-form">
                    <div className="view-invoice-modal-form-group">
                      <label>Amount returned</label>
                      <input
                        type="number"
                        min="1"
                        max={remainingExtra}
                        value={refundAmount}
                        placeholder={`Up to ₹${remainingExtra}`}
                        onChange={(e) => setRefundAmount(e.target.value)}
                      />
                    </div>
                    <div className="view-invoice-modal-form-group">
                      <label>Paid back by</label>
                      <CustomDropdown
                        name="refundMethod"
                        value={refundMethod}
                        options={paymentMethodOptions}
                        onChange={(e) => setRefundMethod(e.target.value)}
                        fullWidth
                      />
                    </div>
                    <div className="view-invoice-modal-form-group">
                      <label>Note</label>
                      <input
                        type="text"
                        value={refundNote}
                        placeholder="Optional, e.g. cash returned at reception"
                        onChange={(e) => setRefundNote(e.target.value)}
                      />
                    </div>
                    <div className="view-invoice-refund-actions">
                      <button
                        type="button"
                        className="view-invoice-refund-cancel"
                        onClick={() => {
                          setShowRefundForm(false);
                          setRefundAmount("");
                          setRefundNote("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="view-invoice-refund-save"
                        onClick={handleRecordRefund}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Saving..." : "Save refund"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showAddPayment && (
              <div className="view-invoice-modal-add-payment-section">
                <h4>Add New Payment</h4>
                <div className="view-invoice-modal-form-group">
                  <label>Payment Method</label>
                  {/* <select
                  className="form-control"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                </select> */}

                  <CustomDropdown

                    name="paymentMethod"
                    value={paymentMethod}
                    options={paymentMethodOptions}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    fullWidth

                  />

                </div>
                <div className="view-invoice-modal-form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => {
                      const val = e.target.value;

                      if (val === "") {
                        setPaymentAmount("");
                        return;
                      }

                      const num = parseFloat(val);
                      if (!isNaN(num) && num > 0) {
                        setPaymentAmount(num);
                      }
                    }}
                    min="1"
                    placeholder="Enter payment amount"
                  />
                  {paymentExceedsBalance && (
                    <p className="view-invoice-modal-extra-note">
                      This amount is greater than the balance due. The extra amount will be recorded as extra balance.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="view-invoice-modal-footer">
            {!showAddPayment ? (
              <>
                <button
                  className="ba-button ba-button-view"
                  onClick={generatePDF}
                >
                  <FaDownload style={{ marginRight: 8 }} />
                  Download Invoice
                </button>

                <button
                  className="ba-button ba-button-view"
                  onClick={sharePDF}
                  style={{ backgroundColor: '#1E88E5', color: 'white' }}
                >
                  <FaShareAlt style={{ marginRight: 8 }} />
                  Share
                </button>

                <button
                  className="ba-button ba-button-secondary"
                  onClick={() => setShowAddPayment(true)}
                >
                  <FaMoneyBillWave style={{ marginRight: 8 }} />
                  Add Payment
                </button>

                <button
                  type="button"
                  className="ba-button cancel-btn"
                  onClick={onClose}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="ba-button cancel-btn"
                  onClick={() => {
                    setShowAddPayment(false);
                    setError(null);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ba-button ba-button-primary"
                  onClick={handleAddPayment}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Processing..." : "Submit Payment"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewInvoiceModal;
